import { Icon } from '@vrplatform/icons';
import { Avatar } from '@mantine/core';
import type { ReactNode } from 'react';
import type { TableBasicRow } from './LazyTable.types';
import type {
  MRT_ColumnDef,
  TableColumnType,
  TableColumnTypeProps,
  TableColumnTypePropsInput,
  TableConnectionEntityValueConfig,
  TableEntityValueConfig,
  TableEntityType,
  TablePrimitiveValueConfig,
  TablePrimitiveType,
  TableTransactionEntityValueConfig,
} from './LazyTableColumns.types';
import {
  TableEntityValue,
  type TableEntityValueProps,
} from './TableEntityValue';
import {
  TablePrimitiveValue,
  type TablePrimitiveValueProps,
} from './TablePrimitiveValue';

const primitiveTypes = new Set<TablePrimitiveType>([
  'date',
  'dateRange',
  'string',
  'longString',
  'accounting',
  'number',
  'address',
]);

const isPrimitiveType = (type: TableColumnType): type is TablePrimitiveType =>
  primitiveTypes.has(type as TablePrimitiveType);

const resolveTypeProps = <TRow extends TableBasicRow>({
  row,
  type,
  typeProps,
  value,
}: {
  row: TRow;
  type: TableColumnType;
  typeProps?: TableColumnTypePropsInput<TRow>;
  value: unknown;
}) => {
  if (typeof typeProps === 'function') {
    return typeProps({ row, type, value });
  }

  return typeProps;
};

const ConnectionEntityIcon = ({
  logoUrl,
}: Pick<TableConnectionEntityValueConfig, 'logoUrl'>) => {
  if (logoUrl) {
    return (
      <Avatar
        src={logoUrl}
        size={12}
        styles={{
          placeholder: { visibility: 'hidden' },
        }}
        sx={(theme) => ({
          border: `1px solid ${theme.colors.gray[2]}`,
        })}
      />
    );
  }

  return <Icon icon="ShareIcon" size={12} />;
};

const renderPrimitiveValue = ({
  primitiveType,
  typeProps,
  value,
}: {
  primitiveType: TablePrimitiveType;
  typeProps?: TableColumnTypeProps;
  value: unknown;
}): ReactNode => {
  switch (primitiveType) {
    case 'date':
      return (
        <TablePrimitiveValue
          {...(typeProps as TablePrimitiveValueConfig<'date'> | undefined)}
          type="date"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'date' }
            >['value']
          }
        />
      );
    case 'dateRange':
      return (
        <TablePrimitiveValue
          {...(typeProps as TablePrimitiveValueConfig<'dateRange'> | undefined)}
          type="dateRange"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'dateRange' }
            >['value']
          }
        />
      );
    case 'string':
      return (
        <TablePrimitiveValue
          {...(typeProps as TablePrimitiveValueConfig<'string'> | undefined)}
          type="string"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'string' }
            >['value']
          }
        />
      );
    case 'longString':
      return (
        <TablePrimitiveValue
          {...(typeProps as
            | TablePrimitiveValueConfig<'longString'>
            | undefined)}
          type="longString"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'longString' }
            >['value']
          }
        />
      );
    case 'accounting':
      return (
        <TablePrimitiveValue
          {...(typeProps as
            | TablePrimitiveValueConfig<'accounting'>
            | undefined)}
          type="accounting"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'accounting' }
            >['value']
          }
        />
      );
    case 'number':
      return (
        <TablePrimitiveValue
          {...(typeProps as TablePrimitiveValueConfig<'number'> | undefined)}
          type="number"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'number' }
            >['value']
          }
        />
      );
    case 'address':
      return (
        <TablePrimitiveValue
          {...(typeProps as TablePrimitiveValueConfig<'address'> | undefined)}
          type="address"
          value={
            value as Extract<
              TablePrimitiveValueProps,
              { type: 'address' }
            >['value']
          }
        />
      );
  }
};

const TransactionEntityIcon = ({
  logoUrl,
}: Pick<TableTransactionEntityValueConfig, 'logoUrl'>) => {
  if (logoUrl) {
    return (
      <Avatar
        src={logoUrl}
        size={14}
        radius={0}
        styles={{
          image: {
            objectFit: 'contain',
          },
          placeholder: { visibility: 'hidden' },
          root: {
            background: 'transparent',
          },
        }}
      />
    );
  }

  return <Icon color="#1343CC" icon="ShareIcon" size={14} />;
};

const renderEntityValue = ({
  entityType,
  typeProps,
  value,
}: {
  entityType: TableEntityType;
  typeProps?: TableColumnTypeProps;
  value: unknown;
}): ReactNode => {
  switch (entityType) {
    case 'listing':
      return (
        <TableEntityValue
          {...(typeProps as TableEntityValueConfig<'listing'> | undefined)}
          type="listing"
          value={
            value as Extract<
              TableEntityValueProps,
              { type: 'listing' }
            >['value']
          }
        />
      );
    case 'owner':
      return (
        <TableEntityValue
          {...(typeProps as TableEntityValueConfig<'owner'> | undefined)}
          type="owner"
          value={
            value as Extract<TableEntityValueProps, { type: 'owner' }>['value']
          }
        />
      );
    case 'transaction': {
      const transactionTypeProps = (typeProps ||
        {}) as TableTransactionEntityValueConfig;
      const { logoUrl, ...tableEntityValueProps } = transactionTypeProps;

      return (
        <TableEntityValue
          {...tableEntityValueProps}
          icon={<TransactionEntityIcon logoUrl={logoUrl} />}
          type="transaction"
          value={
            value as Extract<
              TableEntityValueProps,
              { type: 'transaction' }
            >['value']
          }
        />
      );
    }
    case 'reservation':
      return (
        <TableEntityValue
          {...(typeProps as TableEntityValueConfig<'reservation'> | undefined)}
          type="reservation"
          value={
            value as Extract<
              TableEntityValueProps,
              { type: 'reservation' }
            >['value']
          }
        />
      );
    case 'connection': {
      const connectionTypeProps = (typeProps ||
        {}) as TableConnectionEntityValueConfig;
      const { logoUrl, ...tableEntityValueProps } = connectionTypeProps;

      return (
        <TableEntityValue
          {...tableEntityValueProps}
          icon={<ConnectionEntityIcon logoUrl={logoUrl} />}
          type="connection"
          value={
            value as Extract<
              TableEntityValueProps,
              { type: 'connection' }
            >['value']
          }
        />
      );
    }
    case 'bankAccount':
      return (
        <TableEntityValue
          {...(typeProps as TableEntityValueConfig<'bankAccount'> | undefined)}
          type="bankAccount"
          value={
            value as Extract<
              TableEntityValueProps,
              { type: 'bankAccount' }
            >['value']
          }
        />
      );
    case 'contact':
      return (
        <TableEntityValue
          {...(typeProps as TableEntityValueConfig<'contact'> | undefined)}
          type="contact"
          value={
            value as Extract<
              TableEntityValueProps,
              { type: 'contact' }
            >['value']
          }
        />
      );
  }
};

const renderTypedValue = ({
  type,
  typeProps,
  value,
}: {
  type: TableColumnType;
  typeProps?: TableColumnTypeProps;
  value: unknown;
}) => {
  if (isPrimitiveType(type)) {
    return renderPrimitiveValue({
      primitiveType: type,
      typeProps,
      value,
    });
  }

  return renderEntityValue({
    entityType: type,
    typeProps,
    value,
  });
};

export const withTableTypedColumns = <TRow extends TableBasicRow>(
  columns: MRT_ColumnDef<TRow>[]
) =>
  columns.map((column) => {
    const type = column.type ?? column.meta?.primitiveType;
    const rawTypeProps = column.typeProps ?? column.meta?.primitiveProps;
    let nextColumn = column;

    if (!column.Cell && type) {
      nextColumn = {
        ...nextColumn,
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          const typeProps = resolveTypeProps({
            row: row.original,
            type,
            typeProps: rawTypeProps,
            value,
          });

          return renderTypedValue({
            type,
            typeProps,
            value,
          });
        },
      } satisfies MRT_ColumnDef<TRow>;
    }

    return nextColumn;
  });
